import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Layers, ArrowUpDown, ArrowRight, Search, Calculator, TrendingUp,
  ShieldCheck, CalendarClock, AlertTriangle,
} from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import { fmtPct, fmtMoney, fmtAum, fmtDate } from '@/components/coveredCall/ccFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sector income page — /dividend-paying-<sector>-stocks
 *
 * One component for all 14 pages; the slug comes from the URL and the
 * copy, filters and stats come from /api/sector/{slug}. Universe is
 * operating companies only (no ETFs / CEFs / mutual funds / ADRs).
 */
const COLUMNS = [
  { key: 'ticker', label: 'Company', align: 'left' },
  { key: 'industry', label: 'Industry', align: 'left' },
  { key: 'dividend_yield', label: 'Yield' },
  { key: 'dividend_rate', label: 'Annual $' },
  { key: 'payout_ratio', label: 'Payout', hint: 'Dividend as a share of earnings' },
  { key: 'dividend_cagr_5y', label: 'Div growth', hint: '5-year dividend CAGR' },
  { key: 'streak_years', label: 'Streak', hint: 'Consecutive years of raises, where known' },
  { key: 'quality_score', label: 'Quality', hint: 'Our 0-100 dividend quality score' },
  { key: 'market_cap', label: 'Size' },
  { key: 'perf_1y', label: '1Y' },
];

export default function SectorIncomePage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+|\/+$/g, '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [index, setIndex] = useState([]);
  const [industryPages, setIndustries] = useState([]);
  const [q, setQ] = useState('');
  const [industry, setIndustry] = useState('all');
  const [sort, setSort] = useState({ key: 'market_cap', dir: 'desc' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setData(null);
    setIndustry('all');
    setQ('');
    axios.get(`${API}/sector/${slug}`)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    axios.get(`${API}/sectors`)
      .then((r) => { if (!cancelled) { setIndex(r.data.sectors || []); setIndustries(r.data.industries || []); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  useSeoMeta({
    title: data ? `${data.h1} — ${data.stats.count} companies ranked by yield and quality | DiviDrip` : 'Dividend stocks by sector | DiviDrip',
    description: data?.meta_description || 'Dividend paying stocks by sector, ranked by yield, payout ratio, dividend growth and quality score.',
    canonical: `https://twylightcrow.com/${slug}`,
  });

  const rows = data?.rows || [];

  const industries = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.industry) m.set(r.industry, (m.get(r.industry) || 0) + 1); });
    return [...m.entries()].map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [rows]);

  const visible = useMemo(() => {
    const needle = q.trim().toUpperCase();
    let out = rows.filter((r) => {
      if (industry !== 'all' && r.industry !== industry) return false;
      if (!needle) return true;
      return r.ticker.includes(needle) || (r.name || '').toUpperCase().includes(needle);
    });
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      const av = a[key]; const bv = b[key];
      if (key === 'ticker' || key === 'industry') {
        return String(av || '').localeCompare(String(bv || '')) * mult;
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * mult;
    });
  }, [rows, q, industry, sort]);

  const faqs = useMemo(() => (data ? buildFaqs(data) : []), [data]);

  useEffect(() => {
    if (!data) return;
    const blocks = [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question', name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: data.h1,
        numberOfItems: data.stats.count,
        itemListElement: (data.cuts.highest_yield || []).slice(0, 10).map((r, i) => ({
          '@type': 'ListItem', position: i + 1, name: `${r.ticker} — ${r.name}`,
        })),
      },
    ];
    const nodes = blocks.map((b, i) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(b);
      s.setAttribute('data-testid', `sector-jsonld-${i}`);
      document.head.appendChild(s);
      return s;
    });
    return () => nodes.forEach((n) => n.remove());
  }, [data, faqs]);

  const toggleSort = (key) => setSort((s) => (
    s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: (key === 'ticker' || key === 'industry') ? 'asc' : 'desc' }
  ));

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 space-y-4 animate-pulse" data-testid="sector-loading">
        <div className="h-10 w-2/3 bg-slate-200 dark:bg-slate-800 rounded" />
        <div className="h-40 bg-slate-200 dark:bg-slate-800 rounded-xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="sector-error">
        <BackOrHomeButton />
        <h1 className="mt-6 text-3xl font-black text-slate-900 dark:text-slate-100">Sector not found</h1>
        <Link to="/dividend-stocks-by-sector" className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white font-semibold rounded-lg" data-testid="sector-error-index">
          <Layers className="w-4 h-4" /> All sectors
        </Link>
        <SiteDisclaimer />
      </div>
    );
  }

  const s = data.stats;
  const industryLinks = industryPages.filter((i) => {
    if (i.parent !== data.parent_sector || i.slug === slug || !i.count) return false;
    // Inside Financial Services keep the nav within the same family, so
    // the banks page doesn't offer "P&C Insurance" as a narrower cut.
    if (data.group) return i.parent_group === data.group;
    return true;
  });
  const topSuspect = (data.cuts.highest_yield || []).some(
    (r) => r.ticker === s.top_yield_ticker && r.yield_suspect,
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid={`sector-page-${slug}`}>
      <BackOrHomeButton />

      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-400 font-semibold">
        <Layers className="w-3.5 h-3.5" />
        <Link to="/dividend-stocks-by-sector" className="hover:underline" data-testid="sector-breadcrumb">
          Dividend stocks by sector
        </Link>
        {data.is_financial_split && <span className="text-slate-400 normal-case tracking-normal">· Financial Services</span>}
      </div>

      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-slate-100">
        {data.h1}
      </h1>
      <p className="mt-4 max-w-3xl text-base text-slate-600 dark:text-slate-400 leading-relaxed">{data.intro}</p>
      <p className="mt-3 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
        {s.count} operating companies currently paying a dividend, median yield{' '}
        <strong className="text-slate-900 dark:text-slate-100">{fmtPct(s.median_yield)}</strong>.
        Funds and ETFs are excluded on purpose — this is companies only.
        {data.as_of && <> Prices as of {fmtDate(data.as_of)}.</>}
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sector-stats">
        <Stat label="Dividend payers" value={s.count} testId="sector-stat-count" />
        <Stat label="Median yield" value={fmtPct(s.median_yield)} testId="sector-stat-yield" />
        <Stat
          label="Payout under 60%"
          value={s.count ? `${s.conservative_payouts} of ${s.count}` : '—'}
          hint="Room to keep raising"
          testId="sector-stat-payout"
        />
        <Stat
          label="Highest yield"
          value={s.top_yield_ticker ? `${s.top_yield_ticker} ${fmtPct(s.top_yield_value)}` : '—'}
          hint={topSuspect ? 'Likely a special or one-off payment' : null}
          warn={topSuspect}
          testId="sector-stat-top"
        />
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400" data-testid="sector-substats">
        <Pill>{s.recent_raises} raised recently</Pill>
        {!!s.recent_cuts && <Pill tone="danger">{s.recent_cuts} cut recently</Pill>}
        {!!s.monthly_payers && <Pill>{s.monthly_payers} pay monthly</Pill>}
        {!!s.streaks_known && <Pill>{s.long_streaks} with 10+ year raise streaks (of {s.streaks_known} tracked)</Pill>}
        {!!s.reits && <Pill>{s.reits} REITs</Pill>}
        {!!s.bdcs && <Pill>{s.bdcs} BDCs</Pill>}
        {s.largest_ticker && <Pill>Largest: {s.largest_ticker} {fmtAum(s.largest_market_cap)}</Pill>}
      </div>

      {/* Curated cuts */}
      <section className="mt-12 grid md:grid-cols-2 gap-4 min-w-0" data-testid="sector-cuts">
        <CutCard
          icon={TrendingUp}
          title={`Highest yielding ${data.label.toLowerCase()} dividend stocks`}
          rows={data.cuts.highest_yield}
          metric={(r) => fmtPct(r.dividend_yield)}
          warn
          testId="cut-highest-yield"
        />
        <CutCard
          icon={ShieldCheck}
          title="Safest payouts (low payout, high quality)"
          rows={data.cuts.safest_payouts}
          metric={(r) => `${fmtPct(r.dividend_yield)} · payout ${fmtPct(r.payout_ratio, 0)}`}
          testId="cut-safest"
        />
        <CutCard
          icon={ArrowUpDown}
          title="Fastest dividend growth (5-yr CAGR)"
          rows={data.cuts.fastest_growers}
          metric={(r) => `${fmtPct(r.dividend_cagr_5y, 1)}/yr`}
          testId="cut-growers"
        />
        {data.cuts.monthly_payers?.length ? (
          <CutCard
            icon={CalendarClock}
            title="Monthly dividend payers in this sector"
            rows={data.cuts.monthly_payers}
            metric={(r) => fmtPct(r.dividend_yield)}
            testId="cut-monthly"
          />
        ) : (
          <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800" data-testid="cut-monthly-empty">
            <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100">
              <CalendarClock className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> Monthly payers
            </div>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              No company in this sector pays monthly — the quarterly cheque is the norm here.
              For monthly income, look at REITs, BDCs and specialty finance.
            </p>
            <Link to="/dividend-paying-specialty-finance-stocks" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-cyan-700 dark:text-cyan-400 hover:underline">
              Specialty finance <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </section>

      {/* Main table */}
      <section className="mt-12" data-testid="sector-table-section">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
              All {s.count} {data.label.toLowerCase()} dividend payers
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {visible.length} shown · click any column to sort
            </p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter ticker or name…"
              className="pl-9 pr-3 py-2 w-64 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              data-testid="sector-search"
            />
          </div>
        </div>

        {industries.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="sector-industry-filters">
            <Chip label="All industries" active={industry === 'all'} onClick={() => setIndustry('all')} testId="sector-industry-all" />
            {industries.slice(0, 10).map((i) => (
              <Chip
                key={i.name}
                label={`${i.name} (${i.count})`}
                active={industry === i.name}
                onClick={() => setIndustry(i.name)}
                testId={`sector-industry-${i.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              />
            ))}
          </div>
        )}

        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm" data-testid="sector-table">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    title={c.hint}
                    onClick={() => toggleSort(c.key)}
                    className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 cursor-pointer select-none hover:text-cyan-600 dark:hover:text-cyan-400 whitespace-nowrap ${c.align === 'left' ? 'text-left' : 'text-right'}`}
                    data-testid={`sector-th-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sort.key === c.key && <ArrowUpDown className="w-3 h-3 text-cyan-500" />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.ticker} className="border-t border-slate-100 dark:border-slate-800/70 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`sector-row-${r.ticker}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <Link to={`/stock/${r.ticker}`} className="font-mono font-bold text-cyan-700 dark:text-cyan-400 hover:underline">{r.ticker}</Link>
                      {r.is_reit && <Tag>REIT</Tag>}
                      {r.is_bdc && <Tag>BDC</Tag>}
                      {r.is_mlp && <Tag>MLP</Tag>}
                      {r.recent_change_type === 'raise' && <Tag tone="good">raised</Tag>}
                      {r.recent_change_type === 'cut' && <Tag tone="bad">cut</Tag>}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[220px]">{r.name}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-400 max-w-[160px] truncate">{r.industry || '—'}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                    {fmtPct(r.dividend_yield)}
                    {r.yield_suspect && <AlertTriangle className="inline w-3 h-3 ml-1 text-amber-500" title="Unusually high for this sector — often a special or one-off payment in the trailing rate" />}
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-300 whitespace-nowrap">{fmtMoney(r.dividend_rate)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtPct(r.payout_ratio, 0)}</td>
                  <td className={`px-3 py-2.5 text-right whitespace-nowrap ${r.dividend_cagr_5y == null && r.dividend_growth_1y == null ? 'text-slate-400' : (r.dividend_cagr_5y ?? r.dividend_growth_1y) < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'}`}>
                    {r.dividend_cagr_5y != null
                      ? `${fmtPct(r.dividend_cagr_5y, 1, true)}${r.cagr_is_3y ? '*' : ''}`
                      : r.dividend_growth_1y != null
                        ? `${fmtPct(r.dividend_growth_1y, 1, true)}†`
                        : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">{r.streak_years != null ? `${r.streak_years}y` : '—'}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {r.quality_score != null ? (
                      <span className={`px-2 py-0.5 rounded-md font-bold ${r.quality_score >= 70 ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400' : r.quality_score >= 40 ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}>
                        {r.quality_score}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtAum(r.market_cap)}</td>
                  <td className={`px-3 py-2.5 text-right whitespace-nowrap ${r.perf_1y == null ? 'text-slate-400' : r.perf_1y < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                    {fmtPct(r.perf_1y, 1, true)}
                  </td>
                </tr>
              ))}
              {!visible.length && (
                <tr><td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-slate-500" data-testid="sector-empty">No companies match that filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Operating companies only — ETFs, closed-end funds, ADRs and preferred shares or baby
          bonds are excluded. Payout ratio and
          raise-streak data isn't published for every company; blanks mean we don't have it rather
          than zero. * marks a 3-year growth rate where 5 years isn't available; † marks a
          12-month growth rate shown because the longer rate is distorted by a stock split.
        </p>
      </section>

      {/* Sibling sectors */}
      {!!industryLinks.length && (
        <section className="mt-12" data-testid="sector-industry-pages">
          <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
            Narrower cuts inside {data.parent_sector}
          </h2>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {industryLinks.map((i) => (
              <Link
                key={i.slug}
                to={`/${i.slug}`}
                className="group flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-cyan-400 dark:hover:border-cyan-600 transition-colors"
                data-testid={`sector-industry-page-${i.slug}`}
              >
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{i.label}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
                  {i.count} · {fmtPct(i.median_yield)}
                  <ArrowRight className="inline w-3.5 h-3.5 ml-1 text-slate-400 group-hover:text-cyan-500" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!!index.length && (
        <section className="mt-12" data-testid="sector-siblings">
          <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">Other sectors</h2>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {index.filter((x) => x.slug !== slug).map((x) => (
              <Link
                key={x.slug}
                to={`/${x.slug}`}
                className="group flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-cyan-400 dark:hover:border-cyan-600 transition-colors"
                data-testid={`sector-sibling-${x.slug}`}
              >
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{x.label}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {x.count} · {fmtPct(x.median_yield)}
                  <ArrowRight className="inline w-3.5 h-3.5 ml-1 text-slate-400 group-hover:text-cyan-500" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mt-12 p-6 rounded-xl bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-slate-900 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="sector-cta">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Go deeper</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/screener" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="sector-cta-screener">
            <Search className="w-4 h-4" /> Screen this sector yourself
          </Link>
          <Link to="/dividend-reinvestment-calculator" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="sector-cta-calc">
            <Calculator className="w-4 h-4" /> Project the income
          </Link>
          <Link to="/covered-call-etfs" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="sector-cta-cc">
            <Layers className="w-4 h-4" /> Covered call ETFs
          </Link>
        </div>
      </section>

      <section className="mt-12" data-testid="sector-faq">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Frequently asked</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 group" data-testid={`sector-faq-item-${i}`}>
              <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100 list-none flex items-start justify-between gap-3">
                <span>{f.q}</span>
                <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <SiteDisclaimer />
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */

function Stat({ label, value, hint, warn, testId }) {
  return (
    <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 min-w-0" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-black tabular-nums text-slate-900 dark:text-slate-100 truncate">
        {value ?? '—'}
        {warn && <AlertTriangle className="inline w-4 h-4 ml-1.5 mb-1 text-amber-500" />}
      </div>
      {hint && <div className={`mt-1 text-[10px] ${warn ? 'text-amber-700 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`}>{hint}</div>}
    </div>
  );
}

function Pill({ children, tone }) {
  return (
    <span className={`px-2.5 py-1 rounded-full border ${tone === 'danger'
      ? 'border-rose-300 dark:border-rose-900 text-rose-700 dark:text-rose-400'
      : 'border-slate-200 dark:border-slate-800'}`}>
      {children}
    </span>
  );
}

function Chip({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active
        ? 'bg-cyan-600 border-cyan-600 text-white'
        : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-cyan-400'}`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function Tag({ children, tone }) {
  const cls = tone === 'good'
    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400'
    : tone === 'bad'
      ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400'
      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400';
  return <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${cls}`}>{children}</span>;
}

function CutCard({ icon: Icon, title, rows, metric, warn, testId }) {
  if (!rows?.length) return null;
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 min-w-0 overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100">
        <Icon className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> {title}
      </div>
      <ol className="mt-3 space-y-1.5">
        {rows.map((r, i) => (
          <li key={r.ticker} className="flex items-center justify-between gap-3 text-sm min-w-0">
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] tabular-nums text-slate-400 w-4 shrink-0">{i + 1}</span>
              <Link to={`/stock/${r.ticker}`} className="font-mono font-bold text-cyan-700 dark:text-cyan-400 hover:underline shrink-0">{r.ticker}</Link>
              <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{r.name}</span>
            </span>
            <span className="shrink-0 tabular-nums font-semibold text-slate-800 dark:text-slate-200">
              {metric(r)}
              {warn && r.yield_suspect && <AlertTriangle className="inline w-3 h-3 ml-1 text-amber-500" />}
            </span>
          </li>
        ))}
      </ol>
      {warn && rows.some((r) => r.yield_suspect) && (
        <p className="mt-3 text-[11px] text-amber-700 dark:text-amber-400 flex gap-1.5">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          Flagged yields are unusually high for this sector — usually a special or one-off payment
          still sitting in the trailing rate. Check the payout history before you count on it.
        </p>
      )}
    </div>
  );
}

function buildFaqs(d) {
  const s = d.stats;
  const label = d.label.toLowerCase();
  return [
    {
      q: `How many ${label} stocks pay a dividend?`,
      a: `We currently track ${s.count} ${label} operating companies paying a dividend, with a median yield of ${fmtPct(s.median_yield)}. ETFs, closed-end funds and ADRs are excluded from this count — those are funds, not companies.`,
    },
    {
      q: `Which ${label} stock has the highest dividend yield?`,
      a: s.top_yield_ticker
        ? `${s.top_yield_ticker} shows the highest trailing yield in the sector at ${fmtPct(s.top_yield_value)}. Treat the very top of any yield list with suspicion: an unusually high figure is often a special or one-off payment sitting in the trailing rate, or a share price that has collapsed.`
        : 'We do not have yield data for this sector yet.',
    },
    {
      q: `Are high-yield ${label} dividends safe?`,
      a: `Yield alone tells you nothing about safety. In this sector ${s.conservative_payouts} of ${s.count} companies pay out 60% or less of earnings, which is the group with genuine room to keep raising. ${s.recent_cuts ? `${s.recent_cuts} have cut their dividend recently` : 'None have cut recently'}, and ${s.recent_raises} have raised.`,
    },
    {
      q: `Do any ${label} companies pay monthly dividends?`,
      a: s.monthly_payers
        ? `Yes — ${s.monthly_payers} ${label} companies pay monthly rather than quarterly. They are listed in the monthly payers section above.`
        : `No. Every dividend payer in this sector pays quarterly, semi-annually or annually. Monthly dividends are concentrated in REITs, BDCs and specialty finance.`,
    },
    {
      q: `How often is this list updated?`,
      a: `Nightly. Prices, yields, payout ratios and performance come from our own data pipeline${d.as_of ? `, and this page reflects prices as of ${fmtDate(d.as_of)}` : ''}. New dividend initiations and cuts are picked up automatically.`,
    },
  ];
}
